import { Box, Stack } from '@mui/material'
import GroupCard from 'components/groups/GroupCard'
import {
	getMilkYieldPerHead,
	morphForWords,
	roundingNumber,
} from 'components/shared/processes'
import { IGroupEntity } from 'models'
import moment from 'moment'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { showMilkYieldUi } from 'utils/companyMilkFeatures'
import { TypographyPrimary12 } from 'ui/typography/TypographyPrimary12'
import { TypographyPrimary16Bold } from 'ui/typography/TypographyPrimary16Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

export const DigitizationsIndicators = (props: IGroupEntity) => {
	const { permissions } = useAppSelector(state => state.authReducer)
	const { userData } = useAppSelector(state => state.userReducer)
	const canShowMilkYield = showMilkYieldUi(userData?.company?.id)
	const isPositive =
		props?.milk_yesterday_total > props?.milk_two_days_ago_total
	const yesterdayAvgPerHead = getMilkYieldPerHead(
		props.milk_yesterday_total,
		props.cattles_qty,
	)
	const twoDaysAgoAvgPerHead = getMilkYieldPerHead(
		props.milk_two_days_ago_total,
		props.cattles_qty,
	)
	const differentValue =
		yesterdayAvgPerHead != null && twoDaysAgoAvgPerHead != null
			? yesterdayAvgPerHead - twoDaysAgoAvgPerHead
			: 0

	return (
		<div>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'baseline',
					justifyContent: 'space-between',
					marginBottom: '16px',
				}}
			>
				<TypographyPrimary16Bold>Идентификация</TypographyPrimary16Bold>
				<TypographySecondary14>{`Вчера, ${moment()
					.subtract(1, 'day')
					.format('DD.MM.YYYY')}`}</TypographySecondary14>
			</Box>
			<Stack direction='column' gap='16px'>
				{permissions?.license_identification && canShowMilkYield && (
					<>
						{userData.isIdentificationExpired ? (
							<GroupCard
								bulk='Надой'
								description='Средний на голову'
								customElement={
									<TypographySecondary>Нет данных</TypographySecondary>
								}
							/>
						) : (
							<GroupCard
								bulk='Надой'
								description='Средний на голову'
								customElement={
									<Stack direction='row'>
										{props?.milk_yesterday_total ? (
											<TypographyPrimary16Bold
												sx={{
													color: isPositive ? COLORS.success : COLORS.error,
												}}
											>
												{roundingNumber(
													props.milk_yesterday_total,
													1,
												).toLocaleString()}{' '}
												<span
													style={{
														fontWeight: 400,
														fontSize: '12px',
														color: COLORS.secondary,
													}}
												>
													Л
												</span>{' '}
												<TriangleSpan
													width={20}
													height={10}
													top={!isPositive}
													color={isPositive ? COLORS.success : COLORS.error}
												/>
											</TypographyPrimary16Bold>
										) : (
											<TypographyPrimary16Bold
												sx={{
													color: COLORS.secondary,
												}}
											>
												{0}{' '}
												<span
													style={{
														fontWeight: 400,
														fontSize: '12px',
														color: COLORS.secondary,
													}}
												>
													Л
												</span>{' '}
											</TypographyPrimary16Bold>
										)}
									</Stack>
								}
								customTopDescription={
									<Stack direction='row'>
										{yesterdayAvgPerHead != null ? (
											<TypographyPrimary12>
												{roundingNumber(
													yesterdayAvgPerHead,
													1,
												).toLocaleString()}{' '}
												<span
													style={{
														fontWeight: 400,
														fontSize: '12px',
														color: COLORS.secondary,
													}}
												>
													л
												</span>{' '}
												<span
													style={{
														fontWeight: 400,
														fontSize: '12px',
														color:
															differentValue > 0
																? COLORS.success
																: COLORS.error,
													}}
												>
													{differentValue > 0
														? `+${roundingNumber(differentValue, 1)}`
														: roundingNumber(differentValue, 1)}
												</span>
												<span
													style={{
														fontWeight: 400,
														fontSize: '12px',
														color: COLORS.secondary,
													}}
												>
													л
												</span>
											</TypographyPrimary12>
										) : (
											<TypographySecondary
												sx={{
													color: COLORS.secondary,
												}}
											>
												{0}{' '}
												<span
													style={{
														fontWeight: 400,
														fontSize: '12px',
														color: COLORS.secondary,
													}}
												>
													л
												</span>{' '}
											</TypographySecondary>
										)}
									</Stack>
								}
							/>
						)}
					</>
				)}
				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					{permissions?.license_fatness && (
						<>
							{userData.isFatnessExpired ? (
								<GroupCard
									bulk='Критическая упитанность'
									customElement={
										<TypographySecondary>Нет данных</TypographySecondary>
									}
								/>
							) : (
								<GroupCard
									bulk='Критическая упитанность'
									count={props.fat_cattle}
									descriptionTop={
										props.fat_cattle &&
										props.cattles_qty &&
										props.fat_cattle / props.cattles_qty
											? `${roundingNumber(
													(props.fat_cattle / props.cattles_qty) * 100,
													0,
												)} %`
											: ''
									}
									ratio={
										props.fat_cattle
											? morphForWords(props.fat_cattle, [
													'голова',
													'головы',
													'голов',
												])
											: ''
									}
								/>
							)}
						</>
					)}
					{permissions?.license_limpness && (
						<>
							{userData.isLamenessExpired ? (
								<GroupCard
									bulk='Хромота'
									customElement={
										<TypographySecondary>Нет данных</TypographySecondary>
									}
								/>
							) : (
								<GroupCard
									bulk='Хромота'
									count={props.limp_cattle}
									descriptionTop={
										props.cattles_qty &&
										props.limp_cattle &&
										props.limp_cattle / props.cattles_qty
											? `${roundingNumber(
													(props?.limp_cattle / props?.cattles_qty) * 100,
													0,
												)} %`
											: ''
									}
									ratio={
										props.limp_cattle
											? morphForWords(props.limp_cattle, [
													'голова',
													'головы',
													'голов',
												])
											: ''
									}
								/>
							)}
						</>
					)}
				</Stack>
			</Stack>
		</div>
	)
}

const TriangleSpan = props => {
	const { width, height, color, top } = props
	return (
		<span
			style={{
				width: '0px',
				height: '0px',
				borderLeft: `${width / 2}px solid transparent`,
				borderRight: `${width / 2}px solid transparent`,
				borderTop: top ? `${height}px solid ${color}` : 'none',
				borderBottom: !top ? `${height}px solid ${color}` : 'none',
				display: 'inline-block',
			}}
		></span>
	)
}
